import React, { useEffect, useState } from 'react';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import debounce from '@gumtree/ui-library/src/utils/debounce';
import { ModalSearchBar, NoResultsScreen } from '@gumtree/ui-library/src/select-modal/select-modal.style';
import ClearButton, { useHandleClearButton } from '@gumtree/ui-library/src/input-clear-button/input-clear-button';
import Icon from '@gumtree/ui-library/src/icon/icon';
import RadioList, { ListData } from '@gumtree/ui-library/src/radio-list/radio-list';
import { QuoteServiceFormContainer } from './index.style';
import { ServiceFormButton, ServiceFormContent } from '../../post-a-job-form/service-form/index.style';

export interface SellerSkill {
    disabled: boolean;
    displayValue: string;
    selected: boolean;
    value: string;
}

interface Props {
    sellerSkills: SellerSkill[];
    elName: string;
    onSelectionChange?: (value: string, displayName: string) => void;
    onContinue: () => void;
}

const QuoteServiceForm: React.FC<Props> = ({
    sellerSkills,
    elName,
    onSelectionChange,
    onContinue,
}) => {
    const [internalSelectedValue, setInternalSelectedValue] = useState('');

    const [data, setData] = useState(sellerSkills);
    const [noResults, setNoResults] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        setData(sellerSkills);

        const selected = sellerSkills.find(skill => skill.selected);
        setInternalSelectedValue(selected ? selected.value : '');
        if (inputRef.current) {
            inputRef.current.value = selected ? selected.displayValue : '';
        }
    }, [sellerSkills]);
    
    const handleFilter = (
        noResults: boolean,
        data: ListData = sellerSkills,
    ) => {
        setNoResults(noResults);
        setData(data);
    };

    const filterData = (searchTerm) => {
        const filteredData = sellerSkills.filter((skill) =>
            skill.displayValue.toLowerCase().includes(searchTerm.toLowerCase())
        );
        handleFilter(filteredData.length === 0, filteredData);
    };

    const { inputRef, showClear, onClickClear } = useHandleClearButton('service');

    const handleSearchInput = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        const newSearchTerm = e.target.value;
        setSearchTerm(() => {
            filterData(newSearchTerm);
            return newSearchTerm;
        });
    }, 300);

    const handleClear = () => {
        onClickClear();
        handleFilter(false);
        setSearchTerm('');
    };

    const handleSelectionChange = (value: string) => {
        setInternalSelectedValue(value);
        
        const selectedSkill = data.find(skill => skill.value === value);
        if (selectedSkill) {
            setTimeout(() => {
                if (onSelectionChange) {
                    onSelectionChange(value, selectedSkill.displayValue);
                }
            }, 0);
        }
    };

    const handleDisplayNameChange = (displayName: string) => {
        if (inputRef.current) {
            inputRef.current.value = displayName;
        }
        setSearchTerm(displayName);
        
        if (onSelectionChange && internalSelectedValue) {
            onSelectionChange(internalSelectedValue, displayName);
        }
    };

    const handleContinueClick = () => {
        if (internalSelectedValue && internalSelectedValue !== '') {
            onContinue()
        }
    };

    return (
        <QuoteServiceFormContainer>
            <ServiceFormContent>
                <h2>Select service</h2>
                <ModalSearchBar className="search-bar">
                    <Icon type="magnifying-glass" size="medium" />
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Search"
                        onChange={handleSearchInput}
                    />
                    {showClear && <ClearButton onClick={handleClear} />}
                </ModalSearchBar>
                {noResults ? (
                    <NoResultsScreen {...qaAttribute('no-results-screen')}>
                        <p>
                            No search results for <br /> '{searchTerm}'
                        </p>
                    </NoResultsScreen>
                ) : (
                    <RadioList
                        title=""
                        data={data}
                        listName="attributes[seller_skills]"
                        elName={elName}
                        selectedValue={internalSelectedValue}
                        setSelectedValue={handleSelectionChange}
                        setSelectedDisplayName={handleDisplayNameChange}
                        searchTerm={searchTerm}
                        border
                    />
                )}
            </ServiceFormContent>
            <ServiceFormButton selectedService={internalSelectedValue}>
                <button type="button" onClick={handleContinueClick}>
                    Continue
                </button>
            </ServiceFormButton>
        </QuoteServiceFormContainer>
    );
};

export default QuoteServiceForm;
